import { skipToken } from "@reduxjs/toolkit/query";
import { COPILOT_ID } from "@repo/app-types/entities/copilot";
import type {
  CopilotProposalId,
  CopilotProposalSummary,
} from "@repo/app-types/entities/copilotProposal";
import {
  PlaybookBranch,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import {
  allStaticAgentInstructionTileKeys,
  PlaybookTestResultStatus,
  resolveStaticAgentInstructionTileKey,
  STATIC_AGENT_INSTRUCTION_TILES,
  TeammateAgentBranch,
  type AdminApiPlaybookTestSpecResult,
} from "@repo/app-types/entities/teammateAgent";
import {
  agentInstructionSectionCandidateDraftSchema,
  type AgentInstructionSectionCandidateDraft,
} from "@repo/app-types/entities/voiceAgentConfigurationTools";
import { ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton, iconButton } from "@repo/ui/Buttons/iconButton";
import { ChatComposer } from "@repo/ui/chat/chatComposer";
import {
  ChatEmptyState,
  type ChatSuggestion,
} from "@repo/ui/chat/chatEmptyState";
import { ChatHeader, chatHeaderIconClassName } from "@repo/ui/chat/chatHeader";
import {
  ChatMessage,
  type ChatMessageRenderTextArgs,
  type ChatMessageRenderToolGroupArgs,
} from "@repo/ui/chat/chatMessage";
import { ChatThread } from "@repo/ui/chat/chatThread";
import { ChatThreadSkeleton } from "@repo/ui/chat/chatThreadSkeleton";
import { ChatToolGroup, toolRowsClassName } from "@repo/ui/chat/chatToolCall";
import { messageItemStyle } from "@repo/ui/chat/messageItem";
import type { GradingReferenceWrapper } from "@repo/ui/structuredContent/gradingReferenceContext";
import { ModelOutputContent } from "@repo/ui/structuredContent/modelOutputContent";
import * as Sentry from "@sentry/react";
import {
  IconAlertTriangle,
  IconClock,
  IconPlus,
  IconX,
} from "@tabler/icons-react";
import type { UIMessage } from "ai";
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from "react";
import { ToggleButton } from "react-aria-components";
import type { To } from "react-router";
import { twMerge } from "tailwind-merge";
import {
  useLazyListThreadProposalsQuery,
  useListThreadProposalsQuery,
} from "../../api/entities/copilotProposal.js";
import { useMastraThreadQuery } from "../../api/entities/mastraThread.js";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import {
  useGetTeammateAgentQuery,
  useGetTeammateAgentTestSpecsResultsQuery,
} from "../../api/entities/teammateAgent.js";
import { buildListTag, TagType } from "../../api/helpers/tags.js";
import { api } from "../../api/redux.js";
import { appPaths } from "../../app/appPaths.js";
import { buildPlaybookProposalEditLocation } from "../../components/playbookEditorUrlState.js";
import { useAppDispatch, useAppSelector } from "../../hooks/hook.js";
import { useMastraChatContext } from "../agents/mastra/mastraChatContext.js";
import { checkIsTestCaseProposalData } from "../agents/teammateAgentProposalCardContent.js";
import { TeammateAgentTestCaseProposalCard } from "../agents/teammateAgentTestCaseProposalCard.js";
import { buildEntityResolversFromToolResults } from "../agents/toolResultEntityResolvers.js";
import { ComparePlaybookNodeProposalTool } from "../agents/tools/comparePlaybookNodeProposalTool.js";
import { CountCustomerInteractionsTool } from "../agents/tools/countCustomerInteractionsTool.js";
import { FallbackTool } from "../agents/tools/fallbackTool.js";
import { GetCoachingNotesTool } from "../agents/tools/getCoachingNotesTool.js";
import { GetCriterionDetailsTool } from "../agents/tools/getCriterionDetailsTool.js";
import { GetKbAdherenceFindingTool } from "../agents/tools/getKbAdherenceFindingTool.js";
import { GetKnowledgeDocumentTool } from "../agents/tools/getKnowledgeDocumentTool.js";
import { GetSectionDetailsTool } from "../agents/tools/getSectionDetailsTool.js";
import { GetSignalDetailsTool } from "../agents/tools/getSignalDetailsTool.js";
import { ListCustomerInteractionsTool } from "../agents/tools/listCustomerInteractionsTool.js";
import { ProposeKnowledgeDocumentEditTool } from "../agents/tools/proposeKnowledgeDocumentEditTool.js";
import { ProposePlaybookNodeTool } from "../agents/tools/proposePlaybookNodeTool.js";
import { ProposePlaybookOutcomeTool } from "../agents/tools/proposePlaybookOutcomeTool.js";
import { ResearchCustomerInteractionsTool } from "../agents/tools/researchCustomerInteractionsTool.js";
import { ResearchTeammatesTool } from "../agents/tools/researchTeammatesTool.js";
import { RunVoiceAgentTestsTool } from "../agents/tools/runVoiceAgentTestsTool.js";
import { SearchTeammatesTool } from "../agents/tools/searchTeammatesTool.js";
import { SimulateRolePlayRealismTool } from "../agents/tools/simulateRolePlayRealismTool.js";
import { SimulateVoiceAgentConversationTool } from "../agents/tools/simulateVoiceAgentConversationTool.js";
import { StartAutomationPreviewRunTool } from "../agents/tools/startAutomationPreviewRunTool.js";
import {
  TestPlaybookNodeTool,
  TestPlaybookOutcomeTool,
} from "../agents/tools/testPlaybookTool.js";
import {
  ProposeVoiceAgentInstructionsTool,
  TestVoiceAgentInstructionsTool,
} from "../agents/tools/voiceAgentInstructionsTools.js";
import { AiCreditsExhaustedIndicator } from "../billing/aiCreditsExhaustedIndicator.js";
import { StructuredContentReferenceProviders } from "../structuredContent/structuredContentReferenceProviders.js";
import { CopilotChatMenu } from "./copilotChatMenu.js";
import {
  copilotThreadMetadataSchema,
  FALLBACK_THREAD_TITLE,
  type CopilotChat,
} from "./copilotContext.js";
import {
  CopilotDeepLinkList,
  type CopilotDeepLinkListRow,
} from "./copilotDeepLinkList.js";
import {
  buildCopilotProposalLocation,
  buildCopilotTestResultLocation,
} from "./copilotDeepLinks.js";
import { useNavigateKeepCopilotChat } from "./copilotPageContext.js";
import {
  CopilotProposalCard,
  readCopilotProposalCardContent,
} from "./copilotProposalCard.js";
import {
  countTurnProposals,
  readTurnTestId,
  selectEditorRowToOpen,
  selectThreadTestCaseRuns,
  selectTurnProposals,
  selectTurnTests,
  type PlaybookTurnTest,
  type TeammateAgentTurnTest,
  type TurnCheck,
  type TurnTest,
  type TurnTestCaseRun,
} from "./copilotProposalTurns.js";
import { useCopilotSuggestions } from "./copilotSuggestions.js";
import { selectEditorProposalStatus } from "./editorProposalsSlice.js";

type CopilotChatSurface = "panel" | "modal";

export interface CopilotChatViewProps {
  copilotChat: CopilotChat;
  /**
   * `panel` renders the compact side-panel chat; `modal` renders the expanded
   * chat column with its content centered on a wide rail.
   */
  surface: CopilotChatSurface;
  onRequestClose: () => void;
  /** Panel-only history content below the persistent toolbar. */
  history?: ReactNode;
  isHistoryOpen?: boolean;
  onHistoryOpenChange?: (isOpen: boolean) => void;
  /** Panel-only: open the expanded modal. */
  onRequestExpand?: () => void;
  /**
   * Makes the grading references in the agent's answers interactive. Defaults
   * to linking each one to its interaction in a new tab; a surface already
   * showing that interaction supplies a wrapper that acts on it in place.
   */
  gradingReferenceWrapper?: GradingReferenceWrapper;
}

const modalContentRailClassName = "mx-auto w-full max-w-[760px]";
const modalEmptyStateRailClassName = "mx-auto w-full max-w-[680px]";

/**
 * The copilot conversation: header, message thread, and composer. The
 * same view drives the docked side panel, the expanded modal, and a call
 * transcript's chat.
 */
export const CopilotChatView: FC<CopilotChatViewProps> = (props) => {
  const {
    resourceId,
    threadId,
    threadTitle,
    threadResourceId,
    threadMetadata,
    isThreadNotFound,
  } = props.copilotChat;

  const isModalSurface = props.surface === "modal";
  const isHistoryOpen = props.isHistoryOpen ?? false;
  const isOwnThread = threadResourceId === resourceId;

  // Get the Mastra thread corresponding to the selected thread ID.
  // `currentData` rather than `data`: the latter holds on to the previously
  // selected thread until the new one's fetch settles, which would flash that
  // thread's title in the header and apply its share state to this one.
  const { currentData: currentThread } = useMastraThreadQuery(
    threadId !== undefined ? { agentId: COPILOT_ID, threadId } : skipToken,
  );

  const composerInputRef = useRef<HTMLTextAreaElement | null>(null);

  const chat = useMastraChatContext();
  const lastMessageId = chat.messages.at(-1)?.id;
  const isEmpty = chat.messages.length === 0 && !chat.isLoading;

  // The modal's regular empty state renders the composer inline; every other
  // state (including "chat not found", which has no inline composer) keeps
  // the pinned one.
  const showsPinnedComposer = !(
    isModalSurface &&
    isOwnThread &&
    isEmpty &&
    !isThreadNotFound
  );

  const focusComposer = useCallback(() => {
    const composerInput = composerInputRef.current;
    composerInput?.focus({ preventScroll: true });
    if (document.activeElement === composerInput) {
      return undefined;
    }

    // The focus didn't take, which means an ancestor is still `inert`: the
    // panel clears that when its own ref attaches, and a parent's refs attach
    // after its children's layout effects run. Retry once the commit is done.
    const frame = requestAnimationFrame(() =>
      composerInput?.focus({ preventScroll: true }),
    );
    return () => cancelAnimationFrame(frame);
  }, []);

  // Starting a new chat is the only thing that moves focus here by itself.
  // Comparing against the request the view was built with (rather than a
  // "have I mounted yet" flag) keeps StrictMode's double-invoked effects from
  // counting as a request, so opening a page never steals the user's
  // keystrokes.
  const { composerFocusRequestId } = props.copilotChat;
  const handledFocusRequestIdRef = useRef(composerFocusRequestId);
  useLayoutEffect(() => {
    if (handledFocusRequestIdRef.current === composerFocusRequestId) {
      return undefined;
    }

    handledFocusRequestIdRef.current = composerFocusRequestId;
    return isOwnThread ? focusComposer() : undefined;
  }, [composerFocusRequestId, isOwnThread, focusComposer]);

  // Sending the first message moves the modal's composer out of the empty
  // state and into the footer, destroying the focused textarea. Left alone,
  // the dialog's focus scope hands focus to the header's New chat button,
  // which paints a focus ring there. This only recovers focus the swap
  // orphaned — if it went anywhere else, it stays there.
  const previousShowsPinnedComposerRef = useRef(showsPinnedComposer);
  useLayoutEffect(() => {
    const didComposerMove =
      previousShowsPinnedComposerRef.current !== showsPinnedComposer;
    previousShowsPinnedComposerRef.current = showsPinnedComposer;

    if (!didComposerMove || document.activeElement !== document.body) {
      return undefined;
    }

    return focusComposer();
  }, [showsPinnedComposer, focusComposer]);

  // Falling back through the title the thread was selected with keeps the
  // header on the right name while its own fetch is in flight. An unsent
  // thread and one still awaiting its generated title share the last fallback,
  // so the header holds steady until the real title lands.
  const headerTitle =
    currentThread?.title || threadTitle || FALLBACK_THREAD_TITLE;

  // The freshly fetched thread has the latest share state; validate it rather
  // than trusting the loosely-typed Mastra metadata.
  const currentThreadMetadataParse = copilotThreadMetadataSchema.safeParse(
    currentThread?.metadata,
  );
  const currentThreadMetadata = currentThreadMetadataParse.success
    ? currentThreadMetadataParse.data
    : undefined;

  // Each turn that proposed something lists what it proposed, right there in
  // the transcript. Rows carry live status, so a proposal you later applied or
  // discarded reads as decided without leaving the turn it came from.
  const navigate = useNavigateKeepCopilotChat();
  const { data: threadProposals, isFetching: isFetchingThreadProposals } =
    useListThreadProposalsQuery(
      threadId !== undefined ? { threadId } : skipToken,
    );
  // The same query, fetched fresh on demand: it fills the cache the hook
  // above reads and resolves with the list.
  const [fetchThreadProposals] = useLazyListThreadProposalsQuery();

  // The agent writes proposals server-side mid-turn, which no client mutation
  // observes — so refetch whenever the transcript has more of them than the
  // last fetch accounted for. A layout effect, so the fetch is in flight
  // before the render that added the proposal reaches the screen.
  const turnProposalCount = countTurnProposals(chat.messages);
  useLayoutEffect(() => {
    if (turnProposalCount > 0 && threadId !== undefined) {
      void fetchThreadProposals({ threadId }, false);
    }
  }, [turnProposalCount, threadId, fetchThreadProposals]);

  // A test case run that finished during this session has written results
  // the Testing tab and the toolbar's test pill read, so their agent's results
  // refetch. Runs already in the thread when its history loaded need no
  // refetch, so the count seen only becomes a baseline once the load is over.
  const dispatch = useAppDispatch();
  const threadTestCaseRuns = useMemo(
    () => selectThreadTestCaseRuns(chat.messages),
    [chat.messages],
  );
  const seenTestCaseRunCountRef = useRef(threadTestCaseRuns.length);
  useEffect(() => {
    if (
      chat.isLoading ||
      threadTestCaseRuns.length <= seenTestCaseRunCountRef.current
    ) {
      seenTestCaseRunCountRef.current = threadTestCaseRuns.length;
      return;
    }
    seenTestCaseRunCountRef.current = threadTestCaseRuns.length;
    const agentIds = new Set(
      threadTestCaseRuns.map((run) => run.teammate_agent_id),
    );
    dispatch(
      api.util.invalidateTags(
        [...agentIds].map((agentId) =>
          buildListTag(TagType.PlaybookTestSpecResult, [agentId]),
        ),
      ),
    );
  }, [chat.isLoading, threadTestCaseRuns, dispatch]);

  // The last turn shows its cards once it is over and its proposals have
  // loaded, so a check never shows and then gives way to the proposal that
  // carries it.
  const isLastTurnSettled = !chat.isRunning && !isFetchingThreadProposals;

  const proposalsById = useMemo(
    () => new Map((threadProposals ?? []).map((entry) => [entry.id, entry])),
    [threadProposals],
  );

  // A deep-link row opens its proposal in its editor; the other resource
  // types' cards navigate or apply themselves.
  const openProposalInEditor = (
    proposal: CopilotProposalSummary,
    navigateOptions?: { replace?: boolean },
  ) => {
    switch (proposal.resource_type) {
      case "playbook":
        navigate(
          buildPlaybookProposalEditLocation({
            playbookId: proposal.resource.id,
            proposalId: proposal.id,
          }),
          navigateOptions,
        );
        break;
      case "teammateAgent":
        navigate(
          buildCopilotProposalLocation(
            appPaths.teammateAgentInstructions.build({
              teammateAgentId: proposal.resource.id,
            }),
            proposal.id,
          ),
          navigateOptions,
        );
        break;
      case "knowledgeBaseAttachment":
        navigate(
          buildCopilotProposalLocation(
            appPaths.settingsKnowledgeBaseAttachment.build({
              knowledgeBaseId: proposal.data.knowledge_base_id,
              attachmentId: proposal.resource.id,
            }),
            proposal.id,
          ),
          navigateOptions,
        );
        break;
      case "signal":
      case "automation":
      case "training":
      case "tool": {
        const { review } = readCopilotProposalCardContent(proposal);
        if (review.kind === "open_editor") {
          navigate(review.location, navigateOptions);
        }
        break;
      }
    }
  };

  // When a turn this view watched ends, open what it proposed or tested for
  // the editor's selection. The turn's proposals are fetched first, so the
  // choice never reads a stale list. The turn is identified by the message
  // that started it: switching threads mid-run also ends the run, and the
  // thread that replaces it holds no such message, so it opens nothing.
  const editorFocus = useAppSelector((state) => state.editorProposals.focus);
  const openTurnInEditor = useEffectEvent(
    (
      userMessageId: string,
      proposals: readonly CopilotProposalSummary[] | undefined,
    ) => {
      const lastMessage = chat.messages.at(-1);
      const isWatchedTurn = chat.messages.some(
        (message) => message.id === userMessageId,
      );
      if (!lastMessage || !isWatchedTurn || !editorFocus) {
        return;
      }
      const rowToOpen = selectEditorRowToOpen(
        lastMessage,
        new Map((proposals ?? []).map((entry) => [entry.id, entry])),
        editorFocus,
      );
      switch (rowToOpen?.kind) {
        case "proposal":
          openProposalInEditor(rowToOpen.proposal, { replace: true });
          break;
        case "check":
          navigate(buildTurnTestLocation(rowToOpen.check), {
            replace: true,
          });
          break;
      }
    },
  );
  const readRunningTurnUserMessageId = useEffectEvent(
    () => chat.messages.findLast((message) => message.role === "user")?.id,
  );
  const watchedTurnUserMessageIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (chat.isRunning) {
      watchedTurnUserMessageIdRef.current ??= readRunningTurnUserMessageId();
      return;
    }
    const userMessageId = watchedTurnUserMessageIdRef.current;
    if (userMessageId === undefined || threadId === undefined) {
      return;
    }
    watchedTurnUserMessageIdRef.current = undefined;
    void fetchThreadProposals({ threadId }, false).then(({ data }) => {
      openTurnInEditor(userMessageId, data);
    });
  }, [chat.isRunning, threadId, fetchThreadProposals]);

  const suggestions = useCopilotSuggestions();
  const handleSuggestionSelect = ({ prompt, reply }: ChatSuggestion) => {
    props.copilotChat.onNewThreadSelect(
      reply
        ? { initialExchange: { userText: prompt, replyText: reply } }
        : { initialUserMessageText: prompt },
    );
  };

  const footerClassName = twMerge(
    "shrink-0 bg-card",
    isModalSurface ? "px-6 pt-3 pb-6" : "px-3 pt-2 pb-3",
  );

  const composer = (
    <ChatComposer
      variant="boxed"
      inputRef={composerInputRef}
      placeholder="Ask a question..."
      isRunning={chat.isRunning}
      // Until the thread loads, a send would be overwritten by its history or
      // start a second thread.
      isDisabled={chat.isLoading}
      onStop={chat.stop}
      onSend={chat.sendMessage}
      // Controlled from the provider: the panel and the modal never render at
      // once, so an internally-held draft would be lost moving between them.
      value={props.copilotChat.composerDraft}
      onValueChange={props.copilotChat.onComposerDraftChange}
    />
  );

  const emptyState = isThreadNotFound ? (
    <ThreadNotFoundState />
  ) : isModalSurface && isOwnThread ? (
    // In the modal the empty state centers vertically, with the composer
    // inline underneath the suggestions instead of pinned to the bottom.
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-5">
      <div className={twMerge("flex flex-col", modalEmptyStateRailClassName)}>
        <ChatEmptyState
          suggestions={suggestions}
          onSuggestionSelect={handleSuggestionSelect}
        />
        <div className="mt-8">{composer}</div>
      </div>
    </div>
  ) : (
    <div className="flex flex-1 flex-col items-center px-3 pt-[clamp(5rem,18vh,9rem)] pb-5">
      <ChatEmptyState
        className="w-[calc(100%-1.5rem)] max-w-[420px]"
        suggestions={isOwnThread ? suggestions : []}
        onSuggestionSelect={handleSuggestionSelect}
      />
    </div>
  );

  return (
    <StructuredContentReferenceProviders
      gradingReferenceWrapper={props.gradingReferenceWrapper}
    >
      <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden bg-card">
        <ChatHeader
          className={
            isModalSurface
              ? "border-b border-border-light bg-card px-6 py-4"
              : "bg-card px-3 pt-3 pb-2"
          }
          title={isHistoryOpen ? "Chat history" : headerTitle}
          titleAccessory={
            !isHistoryOpen && !isOwnThread ? (
              <div className="shrink-0 rounded-md bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">
                View only
              </div>
            ) : null
          }
          actions={
            <>
              <AiCreditsExhaustedIndicator surface="askAlpharun" />
              <IconButton
                accessibleLabel="New chat"
                className="hover:bg-muted"
                variant={ButtonVariant.Tertiary}
                icon={<IconPlus className={chatHeaderIconClassName} />}
                onClick={() => {
                  props.onHistoryOpenChange?.(false);
                  if (threadId === undefined && isEmpty && !chat.isRunning) {
                    focusComposer();
                  } else {
                    props.copilotChat.onNewThreadSelect();
                  }
                }}
              />

              {props.onHistoryOpenChange ? (
                <ToggleButton
                  aria-label="Chat history"
                  isSelected={isHistoryOpen}
                  onChange={props.onHistoryOpenChange}
                  className={iconButton({
                    variant: ButtonVariant.Tertiary,
                    className: "hover:bg-muted selected:bg-muted",
                  })}
                >
                  <IconClock className={chatHeaderIconClassName} />
                </ToggleButton>
              ) : null}

              <CopilotChatMenu
                copilotChat={props.copilotChat}
                threadMetadata={currentThreadMetadata ?? threadMetadata}
                onRequestExpand={props.onRequestExpand}
              />

              <IconButton
                accessibleLabel="Close research assistant"
                className="hover:bg-muted"
                variant={ButtonVariant.Tertiary}
                icon={<IconX className={chatHeaderIconClassName} />}
                onClick={props.onRequestClose}
              />
            </>
          }
        />
        <div className="relative flex min-h-0 flex-1 flex-col overflow-clip">
          <div
            ref={(element) => {
              if (element) element.inert = isHistoryOpen;
            }}
            className={twMerge(
              "flex min-h-0 flex-1 flex-col transition-opacity duration-150 motion-reduce:transition-none",
              isHistoryOpen && "pointer-events-none opacity-0",
            )}
          >
            <ChatThread
              className={twMerge("min-h-0 bg-card", toolRowsClassName)}
              messages={chat.messages}
              isLoading={chat.isLoading}
              loadingIndicator={
                <ChatThreadSkeleton
                  className={
                    isModalSurface ? modalContentRailClassName : undefined
                  }
                />
              }
              showsEdgeFades
              timelineClassName={isModalSurface ? "px-6 py-5" : "px-3 py-2"}
              emptyState={emptyState}
              // Turns break wider than the parts within a turn. Without the inner
              // gap, an assistant turn's text and tool-call rows stack flush: the
              // bubble backgrounds that used to separate them are gone on this
              // surface, so spacing has to do that work.
              messagesClassName="gap-4"
              renderMessage={(message) => (
                <div
                  className={twMerge(
                    // `space-y` rather than a flex gap: flex items default to
                    // `min-width: auto`, which stops long markdown (code blocks,
                    // tables, URLs) from shrinking and overflows the panel.
                    "max-w-full min-w-0 space-y-3 break-words [&_a]:wrap-anywhere [&_pre]:max-w-full [&_pre]:whitespace-pre-wrap",
                    isModalSurface && modalContentRailClassName,
                  )}
                >
                  <Sentry.ErrorBoundary fallback={<AssistantMessageError />}>
                    <ChatMessage
                      message={message}
                      isStreaming={
                        chat.isRunning && message.id === lastMessageId
                      }
                      tools={TOOL_COMPONENTS}
                      renderText={renderResearchText}
                      renderToolGroup={renderResearchToolGroup}
                    />
                  </Sentry.ErrorBoundary>
                  {!isLastTurnSettled && message.id === lastMessageId ? null : (
                    <>
                      <TurnProposalList
                        message={message}
                        proposalsById={proposalsById}
                        onSelect={openProposalInEditor}
                      />
                      <TurnTestList
                        message={message}
                        proposalsById={proposalsById}
                        onSelect={(test) => {
                          navigate(buildTurnTestLocation(test));
                        }}
                      />
                    </>
                  )}
                </div>
              )}
              composer={
                !isOwnThread ? (
                  <div
                    className={twMerge(
                      footerClassName,
                      "text-center text-sm text-muted-foreground",
                    )}
                  >
                    This chat is view-only. You cannot send messages.
                  </div>
                ) : showsPinnedComposer ? (
                  <div className={footerClassName}>
                    <div
                      className={
                        isModalSurface ? modalContentRailClassName : undefined
                      }
                    >
                      {composer}
                    </div>
                  </div>
                ) : undefined
              }
            />
          </div>
          {props.history ? (
            <div
              ref={(element) => {
                if (element) element.inert = !isHistoryOpen;
              }}
              className={twMerge(
                "absolute inset-0 flex min-h-0 flex-col bg-card transition-opacity duration-150 motion-reduce:transition-none",
                !isHistoryOpen && "pointer-events-none opacity-0",
              )}
            >
              {props.history}
            </div>
          ) : null}
        </div>
      </div>
    </StructuredContentReferenceProviders>
  );
};

/**
 * The proposals one turn put up for review. Rendered under that turn's
 * message, so the transcript reads as a history of what was proposed and what
 * became of it, rather than one running list of everything outstanding. A
 * proposal either has a card of its own or a row in the deep-link list: a
 * voice agent's test case applies from its card, while its instruction
 * proposals and knowledge base document edits open in their editors.
 */
const TurnProposalList: FC<{
  message: UIMessage;
  proposalsById: ReadonlyMap<string, CopilotProposalSummary>;
  onSelect: (proposal: CopilotProposalSummary) => void;
}> = ({ message, proposalsById, onSelect }) => {
  const proposals = useMemo(
    () => selectTurnProposals(message, proposalsById),
    [message, proposalsById],
  );
  // The row the open editor is showing. The editor reports at most one, so
  // the first match is the only one.
  const activeRowId = useAppSelector((state) =>
    proposals.find(
      (proposal) =>
        selectEditorProposalStatus(state, proposal.id) === "viewing",
    ),
  )?.id;
  if (proposals.length === 0) {
    return null;
  }

  // A row names its playbook or voice agent only when the turn's rows span
  // more than one.
  const playbookIds = new Set(
    proposals.flatMap((proposal) =>
      proposal.resource_type === "playbook" ? [proposal.resource.id] : [],
    ),
  );
  const teammateAgentIds = new Set(
    proposals.flatMap((proposal) =>
      proposal.resource_type === "teammateAgent" ? [proposal.resource.id] : [],
    ),
  );
  const rows: CopilotDeepLinkListRow<CopilotProposalId>[] = [];
  const cards: ReactNode[] = [];
  for (const proposal of proposals) {
    switch (proposal.resource_type) {
      case "signal":
      case "automation":
      case "training":
        cards.push(
          <CopilotProposalCard key={proposal.id} proposal={proposal} />,
        );
        break;
      case "playbook":
        rows.push(buildProposalRow(proposal, playbookIds.size > 1));
        break;
      case "teammateAgent":
        if (checkIsTestCaseProposalData(proposal.data)) {
          cards.push(
            <TeammateAgentTestCaseProposalCard
              key={proposal.id}
              proposal={proposal}
              data={proposal.data}
            />,
          );
        } else {
          rows.push(buildProposalRow(proposal, teammateAgentIds.size > 1));
        }
        break;
      case "knowledgeBaseAttachment":
      case "tool":
        cards.push(
          <CopilotProposalCard key={proposal.id} proposal={proposal} />,
        );
        break;
    }
  }
  return (
    <div className="space-y-3">
      {rows.length > 0 ? (
        <CopilotDeepLinkList
          rows={rows}
          activeRowId={activeRowId}
          onSelect={(row) => {
            const proposal = proposals.find((entry) => entry.id === row.id);
            if (proposal) {
              onSelect(proposal);
            }
          }}
        />
      ) : null}
      {cards}
    </div>
  );
};

/** A proposal's deep-link row, naming its resource where the list spans several. */
function buildProposalRow(
  proposal: CopilotProposalSummary,
  showsResourceName: boolean,
): CopilotDeepLinkListRow<CopilotProposalId> {
  return {
    id: proposal.id,
    resourceType: proposal.resource_type,
    name: proposal.node_name,
    ...(showsResourceName ? { resourceName: proposal.resource.name } : {}),
    status: proposal.status,
  };
}

/**
 * The tests one turn ran, rendered like its proposals. A test has no
 * lifecycle, but a check measured one revision: it opens while that is still
 * the resource's current revision and reads as outdated once the resource
 * changes. One list per playbook or voice agent, so each can check its
 * revision.
 */
const TurnTestList: FC<{
  message: UIMessage;
  proposalsById: ReadonlyMap<string, CopilotProposalSummary>;
  onSelect: (test: TurnTest) => void;
}> = ({ message, proposalsById, onSelect }) => {
  const testsByResource = useMemo(
    () => groupTurnTestsByResource(message, proposalsById),
    [message, proposalsById],
  );
  const showsResourceName = testsByResource.size > 1;

  return (
    <>
      {[...testsByResource.values()].map((group) => {
        switch (group.resource_type) {
          case "playbook":
            return (
              <PlaybookTurnTestList
                key={group.key}
                playbookId={group.resourceId}
                tests={group.tests}
                showsPlaybookName={showsResourceName}
                onSelect={onSelect}
              />
            );
          case "teammateAgent":
            return (
              <TeammateAgentTurnTestList
                key={group.key}
                teammateAgentId={group.resourceId}
                tests={group.tests}
                showsAgentName={showsResourceName}
                onSelect={onSelect}
              />
            );
        }
      })}
    </>
  );
};

type TurnTestGroup =
  | {
      key: string;
      resource_type: "playbook";
      resourceId: PlaybookId;
      tests: PlaybookTurnTest[];
    }
  | {
      key: string;
      resource_type: "teammateAgent";
      resourceId: TeammateId;
      tests: TeammateAgentTurnTest[];
    };

/**
 * A turn's tests by the resource they measured, resources in the order the
 * turn first tested them and each resource's checks ahead of its test case
 * runs.
 */
function groupTurnTestsByResource(
  message: UIMessage,
  proposalsById: ReadonlyMap<string, CopilotProposalSummary>,
): ReadonlyMap<string, TurnTestGroup> {
  const groups = new Map<string, TurnTestGroup>();
  for (const test of selectTurnTests(message, proposalsById)) {
    switch (test.resource_type) {
      case "playbook": {
        const key = `${test.resource_type}:${test.playbook.id}`;
        const group = groups.get(key);
        if (group?.resource_type === "playbook") {
          group.tests.push(test);
        } else {
          groups.set(key, {
            key,
            resource_type: "playbook",
            resourceId: test.playbook.id,
            tests: [test],
          });
        }
        break;
      }
      case "teammateAgent": {
        const key = `${test.resource_type}:${test.teammate_agent_id}`;
        const group = groups.get(key);
        if (group?.resource_type === "teammateAgent") {
          group.tests.push(test);
        } else {
          groups.set(key, {
            key,
            resource_type: "teammateAgent",
            resourceId: test.teammate_agent_id,
            tests: [test],
          });
        }
        break;
      }
    }
  }
  for (const group of groups.values()) {
    group.tests.sort(compareChecksFirst);
  }
  return groups;
}

function compareChecksFirst(a: TurnTest, b: TurnTest): number {
  return Number(a.kind === "testCaseRun") - Number(b.kind === "testCaseRun");
}

/** One resource's test rows, marking the check the open editor shows. */
const ResourceTurnTestList = <T extends TurnTest>({
  tests,
  buildRow,
  onSelect,
}: {
  tests: readonly T[];
  buildRow: (test: T) => CopilotDeepLinkListRow;
  onSelect: (test: T) => void;
}) => {
  const rows = tests.map(buildRow);
  const activeRowId = useAppSelector((state) =>
    tests
      .flatMap((test) => (test.kind === "check" ? [test.test_result_id] : []))
      .find((id) => selectEditorProposalStatus(state, id) === "viewing"),
  );
  return (
    <CopilotDeepLinkList
      title="Test results"
      activeRowId={activeRowId}
      rows={rows}
      onSelect={(row) => {
        const test = tests.find(
          (candidate) => readTurnTestId(candidate) === row.id,
        );
        if (test) {
          onSelect(test);
        }
      }}
    />
  );
};

/**
 * A playbook's checks measured one revision: the draft branch resolves to the
 * revision the editor works on (the draft, falling back to live). Until it
 * loads, rows open; the editor's fetch omits an outdated result anyway.
 */
const PlaybookTurnTestList: FC<{
  playbookId: PlaybookId;
  tests: readonly PlaybookTurnTest[];
  /** Set when the turn's tests span more than one resource. */
  showsPlaybookName: boolean;
  onSelect: (test: PlaybookTurnTest) => void;
}> = ({ playbookId, tests, showsPlaybookName, onSelect }) => {
  const { data: playbook } = usePlaybookByIdQuery({
    id: playbookId,
    branch: PlaybookBranch.Draft,
  });
  return (
    <ResourceTurnTestList
      tests={tests}
      buildRow={(test) => ({
        id: test.test_result_id,
        resourceType: "playbook" as const,
        name: test.node_name,
        ...(showsPlaybookName ? { resourceName: test.playbook.name } : {}),
        ...(playbook && playbook.revision_id !== test.playbook_revision_id
          ? { status: "stale" as const }
          : {}),
      })}
      onSelect={onSelect}
    />
  );
};

/**
 * A voice agent's checks measured the revision they read: the draft, falling
 * back to live. Check rows are named by the tiles the tested candidate
 * changed, or as a check of the saved instructions; a test case run's row by
 * how its cases did, read live so a run the copilot stopped waiting on still
 * settles. It opens the Testing tab, which always shows the latest results,
 * so it never goes stale.
 */
const TeammateAgentTurnTestList: FC<{
  teammateAgentId: TeammateId;
  tests: readonly TeammateAgentTurnTest[];
  /** Set when the turn's tests span more than one resource. */
  showsAgentName: boolean;
  onSelect: (test: TeammateAgentTurnTest) => void;
}> = ({ teammateAgentId, tests, showsAgentName, onSelect }) => {
  const { data: teammateAgent } = useGetTeammateAgentQuery({
    id: teammateAgentId,
    branch: TeammateAgentBranch.Draft,
  });
  const currentRevisionId =
    teammateAgent?.teammate_agent_config_revision_id ??
    teammateAgent?.teammate_agent_config.live_revision_id;
  const resourceName =
    showsAgentName && teammateAgent ? { resourceName: teammateAgent.name } : {};
  const testCaseRunResults = useTestCaseRunResults(
    teammateAgentId,
    teammateAgent?.teammate_agent_config.playbook_id,
    tests.filter((test) => test.kind === "testCaseRun"),
  );
  return (
    <ResourceTurnTestList
      tests={tests}
      buildRow={(test) => {
        switch (test.kind) {
          case "check":
            return {
              id: test.test_result_id,
              resourceType: "teammateAgent" as const,
              name: describeTestedInstructions(test.candidate_draft),
              ...resourceName,
              ...(teammateAgent &&
              currentRevisionId !== test.teammate_agent_config_revision_id
                ? { status: "stale" as const }
                : {}),
            };
          case "testCaseRun":
            return {
              id: test.test_run_id,
              resourceType: "teammateAgent" as const,
              name: describeTestCaseRunResults(
                testCaseRunResults.get(test.test_run_id) ?? test.results,
              ),
              ...resourceName,
            };
        }
      }}
      onSelect={onSelect}
    />
  );
};

/**
 * Where a turn's test opens: a check in its resource's editor, a test case
 * run on the agent's Testing tab.
 */
function buildTurnTestLocation(test: TurnTest): To {
  switch (test.kind) {
    case "check":
      return buildTurnCheckLocation(test);
    case "testCaseRun":
      return appPaths.teammateAgentTesting.build({
        teammateAgentId: test.teammate_agent_id,
      });
  }
}

function buildTurnCheckLocation(check: TurnCheck): To {
  switch (check.resource_type) {
    case "playbook":
      return buildCopilotTestResultLocation(
        appPaths.settingsPlaybookEdit.build({
          playbookId: check.playbook.id,
        }),
        check.test_result_id,
      );
    case "teammateAgent":
      return buildCopilotTestResultLocation(
        appPaths.teammateAgentInstructions.build({
          teammateAgentId: check.teammate_agent_id,
        }),
        check.test_result_id,
      );
  }
}

type TestCaseRunResult = TurnTestCaseRun["results"][number];

/**
 * Each run's per-case results, with the live result the Testing tab reads in
 * place of the tool output's wherever it has one for that case and run. The
 * output froze when the copilot stopped waiting, so a case still in progress
 * there settles here. Polls while a live result of one of these runs is
 * still in progress, as the Testing tab does.
 */
function useTestCaseRunResults(
  teammateAgentId: TeammateId,
  playbookId: PlaybookId | undefined,
  runs: readonly TurnTestCaseRun[],
): ReadonlyMap<string, readonly TestCaseRunResult[]> {
  const [isAwaitingResults, setIsAwaitingResults] = useState(false);
  const { data: specResults } = useGetTeammateAgentTestSpecsResultsQuery(
    runs.length > 0 && playbookId ? { teammateAgentId, playbookId } : skipToken,
    { pollingInterval: isAwaitingResults ? 5000 : 0 },
  );
  const resultsByRunId = useMemo(
    () =>
      new Map(
        runs.map((run) => [
          run.test_run_id,
          run.results.map(
            (result) =>
              readLiveTestCaseResult(specResults, run.test_run_id, result) ??
              result,
          ),
        ]),
      ),
    [runs, specResults],
  );
  const hasInProgressResult = [...resultsByRunId.values()].some((results) =>
    results.some(
      (result) => result.status === PlaybookTestResultStatus.IN_PROGRESS,
    ),
  );
  useEffect(() => {
    setIsAwaitingResults(hasInProgressResult);
  }, [hasInProgressResult]);
  return resultsByRunId;
}

/** The latest result for a case where it belongs to the given run. */
function readLiveTestCaseResult(
  specResults: readonly AdminApiPlaybookTestSpecResult[] | undefined,
  testRunId: string,
  { test_spec_id }: TestCaseRunResult,
): TestCaseRunResult | undefined {
  const live = specResults?.find(
    (specResult) =>
      specResult.playbook_test_spec.id === test_spec_id &&
      specResult.playbook_test_result?.playbook_test_run_id === testRunId,
  )?.playbook_test_result;
  if (!live) {
    return undefined;
  }
  return {
    test_spec_id,
    status: live.status,
    passed:
      live.status === PlaybookTestResultStatus.DONE
        ? (live.output?.verdict?.passed ?? null)
        : null,
  };
}

function describeTestCaseRunResults(
  results: readonly TestCaseRunResult[],
): string {
  const total = results.length;
  const noun = total === 1 ? "test case" : "test cases";
  if (results.some((result) => result.status === "in_progress")) {
    return `${total} ${noun} still running`;
  }
  const passed = results.filter((result) => result.passed === true).length;
  const errored = results.filter((result) => result.status === "error").length;
  return errored > 0
    ? `${passed} of ${total} ${noun} passed, ${errored} did not run`
    : `${passed} of ${total} ${noun} passed`;
}

/** The tiles a tested candidate changed, in tile order, or the saved instructions. */
function describeTestedInstructions(
  candidateDraft: AgentInstructionSectionCandidateDraft | null,
): string {
  const changedSections = candidateDraft
    ? agentInstructionSectionCandidateDraftSchema
        .keyof()
        .options.filter((section) => candidateDraft[section] !== undefined)
    : [];
  const tileKeys = new Set(
    changedSections.map(resolveStaticAgentInstructionTileKey),
  );
  const labels = allStaticAgentInstructionTileKeys
    .filter((tileKey) => tileKeys.has(tileKey))
    .map((tileKey) => STATIC_AGENT_INSTRUCTION_TILES[tileKey].label);
  return labels.length > 0 ? labels.join(", ") : "Instruction checks";
}

const ThreadNotFoundState: FC = () => (
  <div className="flex flex-1 flex-col items-center justify-center p-5">
    <div className="flex flex-col items-center gap-4">
      <IconAlertTriangle className="size-12 text-gray-400" />
      <div className="text-center">
        <h2 className="mb-1 text-base font-semibold text-foreground">
          Chat not found
        </h2>
        <p className="text-sm text-muted-foreground">
          This chat doesn&apos;t exist or you don&apos;t have access to it.
        </p>
      </div>
    </div>
  </div>
);

const TOOL_COMPONENTS = {
  byName: {
    searchTeammatesTool: SearchTeammatesTool,
    researchTeammatesTool: ResearchTeammatesTool,
    countCustomerInteractionsTool: CountCustomerInteractionsTool,
    listCustomerInteractionsTool: ListCustomerInteractionsTool,
    researchCustomerInteractionsTool: ResearchCustomerInteractionsTool,
    getCriterionDetailsTool: GetCriterionDetailsTool,
    getSectionDetailsTool: GetSectionDetailsTool,
    getKnowledgeDocumentTool: GetKnowledgeDocumentTool,
    getKbAdherenceFindingTool: GetKbAdherenceFindingTool,
    getSignalDetailsTool: GetSignalDetailsTool,
    proposeKnowledgeDocumentEditTool: ProposeKnowledgeDocumentEditTool,
    getCoachingNotesTool: GetCoachingNotesTool,
    // Pre-rename key, kept so historical threads render.
    retrieveCoachingNotesTool: GetCoachingNotesTool,
    comparePlaybookNodeProposalTool: ComparePlaybookNodeProposalTool,
    // Pre-rename key, kept so historical threads render.
    testCriterionProposalTool: ComparePlaybookNodeProposalTool,
    proposePlaybookNodeTool: ProposePlaybookNodeTool,
    proposePlaybookOutcomeTool: ProposePlaybookOutcomeTool,
    testPlaybookNodeTool: TestPlaybookNodeTool,
    testPlaybookOutcomeTool: TestPlaybookOutcomeTool,
    testVoiceAgentInstructionsTool: TestVoiceAgentInstructionsTool,
    proposeVoiceAgentInstructionsTool: ProposeVoiceAgentInstructionsTool,
    simulateVoiceAgentConversationTool: SimulateVoiceAgentConversationTool,
    runVoiceAgentTestsTool: RunVoiceAgentTestsTool,
    simulateRolePlayRealismTool: SimulateRolePlayRealismTool,
    startAutomationPreviewRunTool: StartAutomationPreviewRunTool,
  },
  Fallback: FallbackTool,
};

/** Tools whose row carries results the user acts on, so their run never folds. */
const UNFOLDED_TOOL_NAMES: ReadonlySet<string> = new Set([
  "startAutomationPreviewRunTool",
]);

function renderResearchToolGroup(
  args: ChatMessageRenderToolGroupArgs,
): ReactNode {
  const { tools, isActive, children } = args;
  if (tools.some((tool) => UNFOLDED_TOOL_NAMES.has(tool.toolName))) {
    return children;
  }
  return (
    <ChatToolGroup tools={tools} isActive={isActive}>
      {children}
    </ChatToolGroup>
  );
}

// The copilot uses the `agent` layout it shares with the configuration
// assistants: a right-aligned muted user bubble above naked assistant text.
// Both roles render structured content, which styles its own elements, so
// neither bubble opts into the `prose` variant.
const userStyle = messageItemStyle({ role: "user", layout: "agent" });
const assistantStyle = messageItemStyle({
  role: "assistant",
  layout: "agent",
});

/** Renders a copilot text part using the `agent` layout. */
function renderResearchText({
  text,
  role,
  isStreaming,
}: ChatMessageRenderTextArgs) {
  // User prompts go through the same structured content pipeline as the
  // assistant's text, for consistency; user input is about as trusted as model
  // output.
  const style = role === "user" ? userStyle : assistantStyle;
  return (
    <div className={style.row()}>
      <div className={style.bubble()}>
        <ResearchMessageTextContent text={text} isStreaming={isStreaming} />
      </div>
    </div>
  );
}

const ResearchMessageTextContent: FC<{
  text: string;
  isStreaming: boolean;
}> = ({ text, isStreaming }) => {
  const chat = useMastraChatContext();

  // Quotes and references cite interactions and teammates returned by earlier
  // tool calls in the thread, so resolve them against those tool results.
  const { resolveInteractionId, resolveTeammatePlaybookId } = useMemo(
    () => buildEntityResolversFromToolResults(chat.messages),
    [chat.messages],
  );

  // The wrappers that make references interactive come from the surface, via
  // the providers `CopilotChatView` sets around the whole thread.
  return (
    <ModelOutputContent
      text={text}
      isStreaming={isStreaming}
      resolveInteractionId={resolveInteractionId}
      context={{ resolveTeammatePlaybookId }}
    />
  );
};

const AssistantMessageError: FC = () => (
  <div
    className={messageItemStyle({
      role: "assistant",
      layout: "agent",
      isError: true,
    }).bubble({ className: "px-3.5" })}
  >
    Unable to render this message.
  </div>
);
